import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { api, eur, errMsg } from '../api';
import { Card, Empty, inputCls, goldBtn } from '../ui';
import { usePortal } from '../PortalAuth';
import PeopleSuggest, { personLine } from '../report/PeopleSuggest';

const FIELDS = [['lastName', 'Cognome'], ['firstName', 'Nome'], ['localDirectorNumber', 'Codice fiscale'], ['dateOfBirth', 'Data di nascita (AAAA-MM-GG)']];

export default function EsponentiTab() {
  const { refresh } = usePortal();
  const [form, setForm] = useState({ firstName: '', lastName: '', localDirectorNumber: '', dateOfBirth: '' });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState(null);
  const [owned, setOwned] = useState({});
  const navigate = useNavigate();
  useEffect(() => {
    api.get('/cs/prices').then((r) => setPrice(r.data.people_report?.cents)).catch(() => {});
    api.get('/cs/people/reports').then((r) => setOwned(Object.fromEntries(r.data.filter((d) => !d.purged_at).reverse().map((d) => [d.people_id, d.id])))).catch(() => {});
  }, []);

  const run = async (e) => {
    e.preventDefault();
    if (!form.localDirectorNumber && (form.lastName.trim().length < 2 || form.firstName.trim().length < 2)) { toast.error('Inserisci cognome e nome (almeno 2 lettere) oppure il codice fiscale'); return; }
    setBusy(true);
    try {
      const params = Object.fromEntries(Object.entries(form).filter(([, v]) => v));
      const { data } = await api.get('/cs/search/people', { params });
      setRes(data);
    } catch (err) { toast.error(errMsg(err)); setRes({ directors: [] }); } finally { setBusy(false); }
  };

  const [buying, setBuying] = useState(null);
  const buy = async (p) => {
    if (owned[p.peopleId]) { navigate(`/portal/report/persona/${owned[p.peopleId]}`); return; }
    setBuying(p.peopleId);
    try {
      const { data } = await api.post('/cs/people/report', { people_id: p.peopleId, name: `${p.firstName} ${p.lastName}` });
      refresh();
      toast.success(data.reused ? 'Report già acquistato: nessun nuovo addebito' : 'Report esponente acquistato');
      navigate(`/portal/report/persona/${data.id}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBuying(null); }
  };

  return (
    <div data-testid="people-tab-esponenti">
      <p className="text-sm text-[#8A8F97] mb-4">Soci, amministratori e dirigenti di società: cariche, partecipazioni e negatività collegate al ruolo in azienda.</p>
      <Card className="p-6 mb-8">
        <form onSubmit={run} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FIELDS.map(([k, l]) => (
            <div key={k}>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[#7A7F87] mb-2">{l}</label>
              <Input value={form[k]} onChange={(e) => { setForm({ ...form, [k]: e.target.value }); setRes(null); }} className={inputCls} data-testid={`people-${k}`} />
            </div>
          ))}
          {!res && !busy && <PeopleSuggest form={form} onPick={(p) => setRes({ directors: [p] })} />}
          <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
            <Button type="submit" disabled={busy} className={`${goldBtn} h-11 px-8`} data-testid="people-submit">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <><Search size={16} className="mr-2" />Cerca esponenti</>}
            </Button>
          </div>
        </form>
      </Card>
      {res && (
        <Card>
          {(res.directors || []).length === 0 ? <Empty icon={Users} title="Nessun esponente trovato" /> : (
            <div className="divide-y divide-[#1F242C]">
              {res.directors.map((p) => (
                <div key={p.peopleId} className="p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`people-result-${p.peopleId}`}>
                  <div className="flex-1">
                    <p className="text-[#F2F2F2] font-medium">{p.firstName} {p.lastName}{p.country && p.country !== 'IT' && <span className="ml-2 text-[10px] font-mono tracking-wider px-1.5 py-0.5 border border-[#1F242C] rounded-sm text-[#8A8F97]" data-testid={`people-result-country-${p.peopleId}`}>{p.country}</span>}</p>
                    <p className="text-sm text-[#8A8F97]">{p.address?.simpleValue}</p>
                    <p className="text-xs font-mono text-[#5A5F67] mt-1">{personLine(p) || p.peopleId}</p>
                    {p.roles?.length > 0 && <p className="text-xs text-[#8A8F97] mt-1" data-testid={`people-result-roles-${p.peopleId}`}>{p.roles.join(' · ')}</p>}
                    {p.country && p.country !== 'IT' && (p.company?.companyName || p.company?.name) && <p className="text-xs text-[#8A8F97] mt-1" data-testid={`people-result-company-${p.peopleId}`}>{p.company.companyName || p.company.name}</p>}
                  </div>
                  {p.country && p.country !== 'IT' ? <span className="text-xs text-[#5A5F67]" data-testid={`people-foreign-note-${p.peopleId}`}>Report non disponibile per l'estero</span> : (
                  <Button onClick={() => buy(p)} disabled={!!buying} className={`${goldBtn} h-10 px-5`} data-testid={`people-buy-${p.peopleId}`}>{buying === p.peopleId ? <Loader2 className="animate-spin" size={15} /> : owned[p.peopleId] ? 'Apri report esponente' : `Report esponente${price != null ? ` · ${eur(price)}` : ''}`}</Button>)}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
